import { useCallback, useEffect, useRef, useState } from 'react'

// normalizeNumbers: converts spoken digit words to numerals (e.g. "two zero one" → "201")
const DIGIT_WORDS: Record<string, string> = {
  zero: '0',
  oh: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
  eleven: '11',
  twelve: '12',
  thirteen: '13',
  fourteen: '14',
  fifteen: '15',
  sixteen: '16',
  seventeen: '17',
  eighteen: '18',
  nineteen: '19',
  twenty: '20',
  thirty: '30',
  forty: '40',
  fifty: '50',
  sixty: '60',
  seventy: '70',
  eighty: '80',
  ninety: '90',
}

/**
 * Converts spoken number words to digits in a transcript.
 * Handles address patterns like "two zero one" → "201",
 * "forty five" → "45", "oh seven" → "07".
 */
export function normalizeNumbers(text: string): string {
  // Replace compound tens+ones: "twenty one" → "21"
  let result = text.replace(
    /\b(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)[- ](one|two|three|four|five|six|seven|eight|nine)\b/gi,
    (_, tens, ones) =>
      String(
        Number(DIGIT_WORDS[tens.toLowerCase()]) +
          Number(DIGIT_WORDS[ones.toLowerCase()]),
      ),
  )

  // Replace single digit followed by compound: "five twenty three" → "523"
  result = result.replace(
    /\b(zero|oh|one|two|three|four|five|six|seven|eight|nine)\s+(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)[- ]?(one|two|three|four|five|six|seven|eight|nine)?\b/gi,
    (_match, digit, tens, ones) => {
      const d = DIGIT_WORDS[digit.toLowerCase()]
      const t = DIGIT_WORDS[tens.toLowerCase()]
      const o = ones ? DIGIT_WORDS[ones.toLowerCase()] : '0'
      return `${d}${t}${o}`.replace(/0$/, '')
    },
  )

  // Replace consecutive single-digit words: "two zero one" → "201"
  result = result.replace(
    /\b(zero|oh|one|two|three|four|five|six|seven|eight|nine)(\s+(zero|oh|one|two|three|four|five|six|seven|eight|nine))+\b/gi,
    (match) =>
      match
        .split(/\s+/)
        .map((w) => DIGIT_WORDS[w.toLowerCase()] ?? w)
        .join(''),
  )

  // Replace remaining standalone number words
  result = result.replace(
    /\b(zero|oh|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)\b/gi,
    (w) => DIGIT_WORDS[w.toLowerCase()] ?? w,
  )

  return result
}

function getSpeechRecognition(): (new () => any) | null {
  if (typeof window === 'undefined') return null
  return (
    (window as any).SpeechRecognition ??
    (window as any).webkitSpeechRecognition ??
    null
  )
}

type Props = {
  onResult: (transcript: string) => void
  normalize?: boolean
  onPermissionDenied?: () => void
}

type SpeechRecognitionEvent = {
  resultIndex: number
  results: Array<Array<{ transcript: string }>>
}

type SpeechRecognitionErrorEvent = { error: string }

type SpeechRecognitionType = {
  lang: string
  interimResults: boolean
  maxAlternatives: number
  onstart: () => void
  onend: () => void
  onerror: (event: SpeechRecognitionErrorEvent) => void
  onresult: (event: SpeechRecognitionEvent) => void
  start: () => void
  stop: () => void
}

export function useVoiceInput({
  onResult,
  normalize = false,
  onPermissionDenied,
}: Props) {
  const supported = !!getSpeechRecognition()
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef<SpeechRecognitionType | null>(null)
  const onResultRef = useRef(onResult)
  const onPermissionDeniedRef = useRef(onPermissionDenied)

  const isHeldRef = useRef(false)
  const accumulatedRef = useRef('')
  const permissionDeniedShownRef = useRef(false)

  useEffect(() => {
    onResultRef.current = onResult
  }, [onResult])

  useEffect(() => {
    onPermissionDeniedRef.current = onPermissionDenied
  }, [onPermissionDenied])

  const reset = useCallback(() => {
    accumulatedRef.current = ''
  }, [])

  const stop = useCallback(() => {
    isHeldRef.current = false
    recognitionRef.current?.stop()
    setListening(false)
  }, [])

  const start = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition()
    if (!SpeechRecognition) return

    isHeldRef.current = true
    permissionDeniedShownRef.current = false

    // Stop existing recognizer to prevent multiple concurrent instances
    recognitionRef.current?.stop()

    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      setListening(true)
    }
    recognition.onend = () => {
      // Only clear listening state if we're not restarting (button not held)
      if (!isHeldRef.current) {
        setListening(false)
      }
      // Restart recognition if button is still held (auto-timeout occurred)
      if (isHeldRef.current && recognitionRef.current) {
        setTimeout(() => {
          if (isHeldRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start()
            } catch (e) {
              // Recognition already started or error
            }
          }
        }, 200)
      }
    }
    recognition.onerror = (event: any) => {
      if (event.error === 'not-allowed' && !permissionDeniedShownRef.current) {
        permissionDeniedShownRef.current = true
        onPermissionDeniedRef.current?.()
        setListening(false)
      } else if (event.error !== 'no-speech' && !isHeldRef.current) {
        // Only clear listening state if button is not held (not auto-restarting)
        setListening(false)
      }
    }
    recognition.onresult = (event: any) => {
      // Separate final and interim results
      let finalTranscript = ''
      let interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscript += text
        } else {
          interimTranscript += text
        }
      }

      // Append final results to accumulated
      if (finalTranscript) {
        if (accumulatedRef.current && finalTranscript) {
          accumulatedRef.current += ` ${finalTranscript}`
        } else {
          accumulatedRef.current += finalTranscript
        }
      }

      // Display: accumulated + interim
      let display = accumulatedRef.current
      if (interimTranscript) {
        display =
          accumulatedRef.current +
          (accumulatedRef.current ? ' ' : '') +
          interimTranscript
      }

      if (display) {
        const processed = normalize ? normalizeNumbers(display) : display
        onResultRef.current(processed)
      }
    }

    recognitionRef.current = recognition
    recognition.start()
  }, [normalize])

  // Clean up on unmount
  useEffect(
    () => () => {
      recognitionRef.current?.stop()
    },
    [],
  )

  return { supported, listening, start, stop, reset }
}
